import { DOCUMENT, inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { MetaData } from './models';

@Injectable({ providedIn: 'root' })
export class MetaDataService {
    private readonly _titleService = inject(Title);
    private readonly _metaService = inject(Meta);
    private readonly _document = inject(DOCUMENT);

    public updateTitle(title: string): void {
        if (title && this._titleService.getTitle() !== title) {
            this._titleService.setTitle(title);
        }
    }

    public updateDescription(description: string): void {
        this._updateMetaTag('description', description);
        this._updateMetaTagWithName('description', description);
    }

    public setMetaData(metaData: MetaData): void {
        const title = metaData.title;

        this.updateTitle(title);
        this.updateDescription(metaData.description);

        const imageUrl = this._getImageParamsUrl(metaData.imageUrl);

        this._updateMetaTag('og:title', title);
        this._updateMetaTag('og:description', metaData.description);
        this._updateMetaTag('og:image', imageUrl);
        this._updateMetaTag('og:url', metaData.url);
        this._updateMetaTag('og:type', metaData.ogType);

        this._updateMetaTag('twitter:card', 'summary_large_image');
        this._updateMetaTag('twitter:title', title);
        this._updateMetaTag('twitter:description', metaData.description);
        this._updateMetaTag('twitter:image', imageUrl);

        this._updateMetaTagWithName('robots', metaData.noIndex ? 'noindex,nofollow' : 'index,follow');

        this._updateCanonicalUrl(metaData.url);
    }

    private _updateMetaTag(name: string, content?: string): void {
        if (!content) {
            this._metaService.removeTag(`property="${name}"`);
            return;
        }

        if (this._metaService.getTag(`property="${name}"`)) {
            this._metaService.updateTag({ property: name, content });
        } else {
            this._metaService.addTag({ property: name, content });
        }
    }

    private _updateMetaTagWithName(name: string, content?: string): void {
        if (!content) {
            this._metaService.removeTag(`name="${name}"`);
            return;
        }

        if (this._metaService.getTag(`name="${name}"`)) {
            this._metaService.updateTag({ name, content });
        } else {
            this._metaService.addTag({ name, content });
        }
    }

    private _updateCanonicalUrl(url?: string): void {
        const existingCanonicalUrl = this._document.querySelector('link[rel="canonical"]');

        if (existingCanonicalUrl) {
            existingCanonicalUrl.remove();
        }

        if (url) {
            const canonicalLink = this._document.createElement('link');
            canonicalLink.rel = 'canonical';
            canonicalLink.href = url;
            this._document.head.appendChild(canonicalLink);
        }
    }

    private _getImageParamsUrl(imageUrl?: string): string | undefined {
        if (!imageUrl) {
            return undefined;
        }

        /*
            Open Graph image requirements:
            - size: 1200x630
            - format: jpg or png
        */

        const url = new URL(imageUrl);
        url.search = '';
        url.searchParams.set('w', '1200');
        url.searchParams.set('fm', 'jpg');
        url.searchParams.set('fit', 'crop');

        return url.toString();
    }
}
